import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Megaphone, Send, Users, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { rundschreibenAPI } from '@/lib/api';
import { gsmText, smsLaenge } from '@/lib/sms';
import { useAuth } from '@/contexts/AuthContext';
import WillkommensmailKarte from '@/components/WillkommensmailKarte';

/* Rundschreiben (22.09.2026, Omoplata-Parität 3.1): eine Nachricht an ein
   Segment über Push, WhatsApp oder E-Mail. Entwurf → Freigabe-Zentrale →
   gedrosselter Versand mit Zustellstatus je Empfänger. */

const KANAL = { push: 'Push (App)', whatsapp: 'WhatsApp', email: 'E-Mail', sms: 'SMS' };
const ZUSTELL = { gesendet: 'versendet', zugestellt: 'zugestellt', geoeffnet: 'geöffnet', geklickt: 'geklickt', bounce_hart: 'unzustellbar', bounce_weich: 'vorübergehend unzustellbar', geblockt: 'blockiert', spam: 'als Spam markiert', beschwerde: 'als Spam gemeldet', fehler: 'Fehler' , abmeldung: 'abgemeldet', antwort: 'Antwort', unklar: 'unklar (bitte prüfen)', wird_gesendet: 'wird gesendet'};
const TARIF = { free: 'Kostenlos', subscription: 'Abo', payasyougo: 'nach Verbrauch' };
const STATUS = { entwurf: 'wartet auf Freigabe', freigegeben: 'freigegeben', laeuft: 'läuft', fertig: 'fertig', verworfen: 'verworfen' };

export default function RundschreibenPage() {
  const navigate = useNavigate();
  const [opt, setOpt] = useState(null);
  const [liste, setListe] = useState([]);
  const [segment, setSegment] = useState({ art: 'alle', kurs_id: '', plan: '' });
  const [kanal, setKanal] = useState('push');
  const [titel, setTitel] = useState('');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [vorschau, setVorschau] = useState(null);
  const [busy, setBusy] = useState(false);
  // Service-Mitteilung (28.09.2026): E-Mail ohne Marketing-Einwilligung, z. B. neue App - nur Inhaber
  const { user } = useAuth();
  const istAdmin = user?.role === 'admin';
  const [service, setService] = useState(false);
  const svc = service && ['email', 'sms'].includes(kanal);
  const smsL = kanal === 'sms' ? smsLaenge(gsmText(text).replace('{vorname}', 'Maximiliane')) : null;
  const [testNummer, setTestNummer] = useState('');

  /* Service-Mail (28.09.2026): HTML-Vorschau in neuem Fenster, Zustellnachweis aus den Brevo-Rueckmeldungen */
  const [zustellung, setZustellung] = useState({});
  const mailVorschau = async (r) => {
    try {
      const html = (await rundschreibenAPI.vorschauHtml(r.id)).data;
      const w = window.open('', '_blank');
      if (w) { w.document.open(); w.document.write(html); w.document.close(); } else toast.error('Popup blockiert');
    } catch { toast.error('Keine HTML-Fassung vorhanden'); }
  };
  const testmail = async (r) => {
    try { const t = (await rundschreibenAPI.testmail(r.id)).data; toast.success(`Testmail an ${t.an}: ${t.status}`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Testmail fehlgeschlagen'); }
  };
  /* SMS (06.10.2026): Test an eine Handynummer über Brevo, genau wie die Mitglieder sie bekommen */
  const testsms = async (r) => {
    if (!testNummer.trim()) { toast.error('Bitte eine Handynummer eintragen'); return; }
    try { const t = (await rundschreibenAPI.testsms(r.id, testNummer.trim())).data; (t.status === 'gesendet' ? toast.success : toast.error)(`Test-SMS an ${t.an}: ${t.status}${t.grund ? ` (${t.grund})` : ''}`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Test-SMS fehlgeschlagen'); }
  };
  const empfaengerAktualisieren = async (r) => {
    try {
      const e = (await rundschreibenAPI.empfaengerAktualisieren(r.id)).data;
      toast.success(`${e.gesamt} Empfänger` + (e.dazu.length ? ` · neu: ${e.dazu.join(', ')}` : '') + (e.weg.length ? ` · raus: ${e.weg.join(', ')}` : ''), { duration: 8000 });
      lade();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Aktualisieren fehlgeschlagen'); }
  };
  const zustellungLaden = async (r) => {
    try { const z = (await rundschreibenAPI.zustellung(r.id)).data; setZustellung(p => ({ ...p, [r.id]: z })); }
    catch { toast.error('Zustellung konnte nicht geladen werden'); }
  };

  /* Brevo-Abgleich (06.10.2026): Guthaben aus dem Konto und Zustellstand je Nachricht aus der Brevo-API
     gegen das CRM-Protokoll. Übernehmen schreibt den Brevo-Stand ins Protokoll, nie etwas zu Brevo. */
  const [konto, setKonto] = useState(null);
  const [kontoFehler, setKontoFehler] = useState('');
  const [kontoBusy, setKontoBusy] = useState(false);
  const [abgleich, setAbgleich] = useState({});
  const [abgleichBusy, setAbgleichBusy] = useState('');
  const kontoLaden = useCallback(async (neu = false) => {
    setKontoBusy(true);
    try { setKonto((await rundschreibenAPI.brevoKonto(neu)).data); setKontoFehler(''); }
    catch (e) { setKontoFehler(e?.response?.data?.detail || 'Brevo-Konto nicht erreichbar'); }
    finally { setKontoBusy(false); }
  }, []);
  useEffect(() => { if (istAdmin) kontoLaden(false); }, [istAdmin, kontoLaden]);
  const abgleichen = async (r, uebernehmen = false) => {
    setAbgleichBusy(r.id);
    try {
      const a = (await rundschreibenAPI.abgleich(r.id, uebernehmen)).data;
      setAbgleich(p => ({ ...p, [r.id]: a }));
      if (uebernehmen) { toast.success(`${a.uebernommen} Zustellstände aus Brevo übernommen`); zustellungLaden(r); lade(); }
      else if (a.abweichungen.length === 0 && a.nur_crm.length === 0) toast.success('CRM und Brevo stimmen überein');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Abgleich fehlgeschlagen'); }
    finally { setAbgleichBusy(''); }
  };

  const lade = useCallback(async () => {
    try {
      const [o, l] = await Promise.all([rundschreibenAPI.optionen(), rundschreibenAPI.liste()]);
      setOpt(o.data); setListe(l.data.rundschreiben || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Konnte nicht laden'); }
  }, []);
  useEffect(() => { lade(); }, [lade]);

  useEffect(() => {
    const h = setTimeout(async () => {
      try { setVorschau((await rundschreibenAPI.vorschau({ segment: { ...segment, kurs_id: segment.kurs_id || null, plan: segment.plan || null }, kanal, service: svc })).data); }
      catch { setVorschau(null); }
    }, 300);
    return () => clearTimeout(h);
  }, [segment, kanal, svc]);

  const VORLAGE_EINWILLIGUNG = { titel: 'Neuigkeiten & Angebote — bist du dabei?', text: 'Hi {vorname}! Wir würden dich gern per WhatsApp und E-Mail über Kursplan, Events, Prüfungen und Aktionen informieren. Tipp hier und gib uns in der App dein OK — dauert 10 Sekunden. Dein P1-Team' };
  const einwilligungVorlage = () => { setKanal('push'); setSegment({ art: 'ohne_einwilligung', kurs_id: '', plan: '' }); setTitel(VORLAGE_EINWILLIGUNG.titel); setText(VORLAGE_EINWILLIGUNG.text); };
  const istEinwilligung = kanal === 'push' && segment.art === 'ohne_einwilligung';

  const anlegen = async () => {
    setBusy(true);
    try {
      const r = await rundschreibenAPI.anlegen({ titel, text, betreff: betreff || null, kanal, segment: { ...segment, kurs_id: segment.kurs_id || null, plan: segment.plan || null }, push_ziel: istEinwilligung ? 'einwilligung' : null, service: svc });
      toast.success(`Entwurf mit ${r.data.zaehler.gesamt} Empfängern liegt in der Freigabe-Zentrale`);
      setTitel(''); setBetreff(''); setText(''); lade();
      navigate(`/freigabe?id=${r.data.freigabe_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Konnte nicht anlegen'); }
    finally { setBusy(false); }
  };

  const ok = titel.trim().length > 1 && text.trim().length >= 5 && vorschau?.empfaenger?.length > 0;

  return (
    <div className="space-y-5 fade-in" data-testid="rundschreiben-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Megaphone className="w-6 h-6 text-primary" /> Rundschreiben</h1>
        <p className="text-muted-foreground text-sm">Eine Nachricht an eine Gruppe — Neuigkeiten, Events, Prüfungsinfos. Geht erst nach Freigabe raus, einzeln und gedrosselt.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="border-border/60 lg:col-span-2">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Neues Rundschreiben</CardTitle>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={einwilligungVorlage} data-testid="rs-vorlage-einwilligung">Vorlage: Einwilligung einholen</Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">An wen</label>
                <select value={segment.art} onChange={(e) => setSegment({ art: e.target.value, kurs_id: '', plan: '' })} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="rs-segment">
                  {(opt?.segmente || []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                {segment.art === 'kurs' && (
                  <select value={segment.kurs_id} onChange={(e) => setSegment((s) => ({ ...s, kurs_id: e.target.value }))} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm mt-1" data-testid="rs-kurs">
                    <option value="">Kurs wählen…</option>
                    {(opt?.kurse || []).map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                  </select>
                )}
                {segment.art === 'plan' && (
                  <select value={segment.plan} onChange={(e) => setSegment((s) => ({ ...s, plan: e.target.value }))} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm mt-1" data-testid="rs-plan">
                    <option value="">Tarif wählen…</option>
                    {(opt?.plaene || []).map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Kanal</label>
                <div className="flex gap-1.5">
                  {Object.entries(KANAL).map(([k, l]) => (
                    <Button key={k} type="button" size="sm" variant={kanal === k ? 'default' : 'outline'} onClick={() => setKanal(k)} data-testid={`rs-kanal-${k}`}>{l}</Button>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground">{kanal === 'push' ? 'An alle mit App auf dem Gerät.' : kanal === 'sms' ? (svc ? 'Service-Mitteilung per SMS über Brevo: an alle mit deutscher Handynummer, jede Nummer einmal. Absender ist der Name „P1Center“, antworten geht nicht.' : 'SMS über Brevo, nur an Mitglieder mit Marketing-Einwilligung und deutscher Handynummer. Absender „P1Center“, antworten geht nicht.') : svc ? 'Service-Mitteilung: an alle Mitglieder mit E-Mail, jede Adresse einmal.' : 'Nur an Mitglieder mit Marketing-Einwilligung.'}</p>
                {['email', 'sms'].includes(kanal) && istAdmin && (
                  <label className="flex items-start gap-2 text-[11px] cursor-pointer pt-1" data-testid="rs-service">
                    <input type="checkbox" className="mt-0.5 accent-marke" checked={service} onChange={(e) => setService(e.target.checked)} />
                    <span>Service-Mitteilung zum Vertrag (z. B. neue App, Öffnungszeiten), ohne Werbe-Einwilligung. Keine Angebote oder Aktionen, sonst ist es Werbung.</span>
                  </label>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Titel (intern{kanal === 'push' ? ' und Push-Überschrift' : ''})</label>
              <Input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="z. B. Gürtelprüfung 18.10." data-testid="rs-titel" />
            </div>
            {kanal === 'email' && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Betreff</label>
                <Input value={betreff} onChange={(e) => setBetreff(e.target.value)} placeholder="leer = Titel" data-testid="rs-betreff" />
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Text · Platzhalter {'{vorname}'}</label>
              <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder="Hi {vorname}, am 18.10. ist Gürtelprüfung …" data-testid="rs-text" />
              {smsL && (
                <p className={`text-[11px] ${smsL.unicode || smsL.teile > 2 ? 'text-amber-700' : 'text-muted-foreground'}`} data-testid="rs-sms-laenge">
                  {smsL.zeichen} Zeichen mit langem Vornamen{smsL.teile > 0 ? `, ${smsL.teile === 1 ? 'eine SMS' : `${smsL.teile} SMS`} je Empfänger` : ''}
                  {smsL.unicode ? '. Emoji oder Sonderzeichen: bitte weglassen, sonst passen nur 70 Zeichen in eine SMS.' : smsL.teile > 2 ? '. Höchstens zwei SMS je Empfänger.' : ''}
                </p>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                {vorschau ? <><b>{vorschau.empfaenger.length}</b> Empfänger
                  {vorschau.ohne_einwilligung.length > 0 && <span className="text-muted-foreground"> · {vorschau.ohne_einwilligung.length} ohne Einwilligung</span>}
                  {vorschau.ohne_kontakt.length > 0 && <span className="text-muted-foreground"> · {vorschau.ohne_kontakt.length} ohne {kanal === 'push' ? 'Gerät' : 'Kontakt'}</span>}</> : '…'}
              </p>
              <Button onClick={anlegen} disabled={!ok || busy} className="gap-1.5" data-testid="rs-anlegen"><Send className="w-4 h-4" /> Zur Freigabe</Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
        {istAdmin && (
          <Card className="border-border/60" data-testid="rs-brevo-konto">
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base">Brevo-Konto</CardTitle>
              <Button size="sm" variant="ghost" onClick={() => kontoLaden(true)} disabled={kontoBusy} title="Bei Brevo neu abfragen"><RefreshCw className={`w-3.5 h-3.5 ${kontoBusy ? 'animate-spin' : ''}`} /></Button>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              {kontoFehler ? <p className="text-amber-700 text-xs">{kontoFehler}</p> : !konto ? <p className="text-muted-foreground text-xs">…</p> : (
                <>
                  <p><b>{konto.sms_guthaben ?? '–'}</b> SMS-Guthaben{konto.sms_moeglich != null && <span className="text-muted-foreground"> · reicht für {konto.sms_moeglich} SMS mit zwei Teilen ({konto.guthaben_je_teil} Guthaben je Teil nach Deutschland)</span>}</p>
                  <p><b>{konto.mail_kontingent ?? '–'}</b> E-Mails{konto.mail_kontingent_art === 'sendLimit' ? ' im Kontingent' : ''}{konto.mail_tarif ? <span className="text-muted-foreground"> · Tarif {TARIF[String(konto.mail_tarif).toLowerCase()] || konto.mail_tarif}</span> : null}</p>
                  <p className="text-[11px] text-muted-foreground">Stand {new Date(konto.abgefragt_am).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}{konto.aus_gedaechtnis ? ' (zwischengespeichert, Pfeil fragt neu)' : ''}{konto.konto_email ? ` · ${konto.konto_email}` : ''}</p>
                </>
              )}
            </CardContent>
          </Card>
        )}
        <Card className="border-border/60">
          <CardHeader className="pb-2 flex-row items-center justify-between"><CardTitle className="text-base">Bisherige</CardTitle><Button size="sm" variant="ghost" onClick={lade}><RefreshCw className="w-3.5 h-3.5" /></Button></CardHeader>
          <CardContent className="p-0">
            {liste.length === 0 ? <p className="p-4 text-sm text-muted-foreground">Noch keins.</p> : (
              <ul className="divide-y divide-border/40 max-h-[520px] overflow-y-auto">
                {liste.map((r) => (
                  <li key={r.id} className="px-4 py-2.5 text-sm" data-testid={`rs-${r.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium truncate">{r.titel}</p>
                      <Badge variant={r.status === 'fertig' ? 'default' : 'outline'} className="text-[10px]">{STATUS[r.status] || r.status}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{KANAL[r.kanal]}{r.service ? ' · Service-Mitteilung' : ''} · {r.zaehler.gesendet}/{r.zaehler.gesamt} gesendet{r.zaehler.fehler ? ` · ${r.zaehler.fehler} Fehler` : ''}{r.zaehler.unklar ? ` · ${r.zaehler.unklar} unklar` : ''} · {new Date(r.erstellt_am).toLocaleDateString('de-DE')}</p>
                    {r.unklar_empfaenger?.length > 0 && (
                      /* 16.10.2026: „unklar“ = vielleicht raus (Antwort blieb aus, Neustart mitten im Senden). Wird nie von selbst wiederholt. */
                      <div className="text-xs text-amber-700" data-testid={`rs-unklar-${r.id}`}>
                        <p>Versand unklar, wird nicht von selbst wiederholt. Bitte nachsehen, bevor du nachsendest:</p>
                        {r.unklar_empfaenger.map((e, i) => <p key={`${e.name}-${i}`} className="pl-2">{e.name || '?'}{e.grund ? ` (${e.grund})` : ''}</p>)}
                      </div>
                    )}
                    {r.lauf_wartet && ['freigegeben', 'laeuft'].includes(r.status) && (
                      <p className="text-xs text-amber-700" data-testid={`rs-wartet-${r.id}`}>Wartet gerade: {r.lauf_wartet}{r.lauf_wartet_seit ? ` (seit ${new Date(r.lauf_wartet_seit).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})` : ''}</p>
                    )}
                    {r.kanal === 'sms' && (
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-xs">
                        {r.status === 'entwurf' && <button type="button" className="text-primary hover:underline" onClick={() => empfaengerAktualisieren(r)}>Empfänger aktualisieren</button>}
                        {istAdmin && <span className="flex items-center gap-1"><Input value={testNummer} onChange={(e) => setTestNummer(e.target.value)} placeholder="+49 … für Test-SMS" className="h-7 w-44 text-xs" data-testid={`rs-testnummer-${r.id}`} /><button type="button" className="text-primary hover:underline" onClick={() => testsms(r)}>Test-SMS</button></span>}
                        {r.zaehler.gesendet > 0 && <button type="button" className="text-primary hover:underline" onClick={() => zustellungLaden(r)}>Zustellung (Brevo)</button>}
                      </div>
                    )}
                    {r.kanal === 'email' && (
                      <div className="flex gap-3 mt-1 text-xs">
                        {r.status === 'entwurf' && <button type="button" className="text-primary hover:underline" onClick={() => empfaengerAktualisieren(r)}>Empfänger aktualisieren</button>}
                        {r.service && <button type="button" className="text-primary hover:underline" onClick={() => mailVorschau(r)}>Mail-Vorschau</button>}
                        <button type="button" className="text-primary hover:underline" onClick={() => testmail(r)}>Testmail an mich</button>
                        {r.zaehler.gesendet > 0 && <button type="button" className="text-primary hover:underline" onClick={() => zustellungLaden(r)}>Zustellung (Brevo)</button>}
                      </div>
                    )}
                    {zustellung[r.id] && (
                      <div className="mt-1 text-xs rounded-md bg-muted/40 px-2 py-1.5" data-testid={`rs-zustellung-${r.id}`}>
                        {Object.entries(zustellung[r.id].je_status).map(([k, n]) => <span key={k} className="mr-3"><b>{n}</b> {ZUSTELL[k] || k}</span>)}
                        {zustellung[r.id].empfaenger.filter(e => !['zugestellt', 'geoeffnet', 'geklickt', 'gesendet'].includes(e.status)).slice(0, 20).map((e, i) => <p key={`${e.an}-${i}`} className="text-muted-foreground">{e.an}: {ZUSTELL[e.status] || e.status}{e.grund ? ` (${e.grund})` : ''}</p>)}
                      </div>
                    )}
                    {istAdmin && ['sms', 'email'].includes(r.kanal) && r.zaehler.gesendet > 0 && (
                      <div className="mt-1 text-xs" data-testid={`rs-abgleich-${r.id}`}>
                        <div className="flex flex-wrap items-center gap-3">
                          <button type="button" className="text-primary hover:underline disabled:opacity-50" disabled={!!abgleichBusy} onClick={() => abgleichen(r, false)}>{abgleichBusy === r.id ? 'Frage Brevo …' : 'Abgleich mit Brevo'}</button>
                          {!abgleich[r.id] && r.brevo_abgleich && (
                            <span className="text-muted-foreground">zuletzt {new Date(r.brevo_abgleich.am).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}: {r.brevo_abgleich.abweichungen} Abweichungen{r.brevo_abgleich.uebernommen ? `, ${r.brevo_abgleich.uebernommen} übernommen` : ''}</span>
                          )}
                        </div>
                        {abgleich[r.id] && (() => { const a = abgleich[r.id]; return (
                          <div className="mt-1 rounded-md border border-border/60 px-2 py-1.5 space-y-1">
                            <p><b>Brevo:</b> {Object.keys(a.brevo.je_status).length === 0 ? 'keine Ereignisse im Zeitraum' : Object.entries(a.brevo.je_status).map(([k, n]) => `${n} ${ZUSTELL[k] || k}`).join(' · ')}</p>
                            <p><b>CRM:</b> {Object.entries(a.crm.je_status).map(([k, n]) => `${n} ${ZUSTELL[k] || k}`).join(' · ')}</p>
                            {a.guthaben && <p className="text-muted-foreground">Guthaben laut CRM: {a.guthaben.geschaetzt} ({a.guthaben.teile} SMS-Teile × {a.guthaben.je_teil} Guthaben), die Wahrheit steht im Brevo-Konto.</p>}
                            {a.brevo.gekappt && <p className="text-amber-700">Brevo liefert höchstens 90 Tage zurück; Rückmeldungen vom Anfang dieses Rundschreibens fehlen deshalb.</p>}
                            {a.brevo.unvollstaendig && <p className="text-amber-700">Brevo hat mehr Rückmeldungen, als auf einmal abgeholt werden konnten; die neuesten fehlen.</p>}
                            {a.abweichungen.length === 0 && a.nur_crm.length === 0 ? <p className="text-emerald-700">Stimmt überein.</p> : (
                              <>
                                {a.abweichungen.slice(0, 30).map(x => <p key={x.id} className={x.uebernommen ? 'text-emerald-700' : ''}>{x.name || '?'} ({x.an}): CRM {ZUSTELL[x.crm] || x.crm} → Brevo {ZUSTELL[x.brevo] || x.brevo}{x.grund ? ` (${x.grund})` : ''}{x.uebernommen ? ' ✓' : !x.uebernehmbar ? ' · bleibt (CRM weiß mehr)' : ''}</p>)}
                                {a.abweichungen.length > 30 && <p className="text-muted-foreground">… und {a.abweichungen.length - 30} weitere</p>}
                                {a.nur_crm.length > 0 && <p className="text-muted-foreground">{a.nur_crm.length} {a.brevo.gekappt || a.brevo.unvollstaendig ? 'ohne geladene Rückmeldung' : 'ohne Rückmeldung von Brevo'}: {a.nur_crm.slice(0, 10).map(x => x.name || x.an).join(', ')}{a.nur_crm.length > 10 ? ' …' : ''}</p>}
                              </>
                            )}
                            {a.nur_brevo > 0 && <p className="text-muted-foreground">{a.nur_brevo} Nachrichten kennt nur Brevo (nicht im CRM-Protokoll).</p>}
                            {a.uebernehmbar > 0 && !a.uebernommen && <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!!abgleichBusy} onClick={() => abgleichen(r, true)} data-testid={`rs-abgleich-uebernehmen-${r.id}`}>Zustellstand von {a.uebernehmbar} Nachrichten übernehmen</Button>}
                          </div>
                        ); })()}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        </div>
      </div>

      {istAdmin && <WillkommensmailKarte />}
    </div>
  );
}
